/**
 * Point d'entrée principal pour la gestion du jeu en temps réel
 * Architecture simple et claire
 */

import { logger } from '@/utils/logger';
import { createConnections } from './connection.js';
import { GAME_ACTIONS } from './constants.js';
import { processMessage } from './message-processor.js';
import { canUpdateIncrementally, updateGameState } from './state-updater.js';

/**
 * Classe principale pour gérer le jeu en temps réel
 * Simple et facile à comprendre
 */
export class GameRealtimeManager {
  constructor() {
    this.chatConnection = null;
    this.gameConnection = null;
    this.currentModel = null;
    this.callbacks = {};
    this.isInitialized = false;
    this.isFetchingModel = false;
    // Timeouts pour nettoyage
    this.fetchModelTimeoutId = null;
    this.sequenceCheckTimeoutId = null;
    this.delayedBallUpdateTimeoutId = null;
    this.navigationTimeoutId = null;
    this.statusIconTimeoutId = null;
    // Protection contre les messages dupliqués
    this.lastProcessedMessages = new Map(); // Map<messageId, timestamp>
    this.DUPLICATE_MESSAGE_WINDOW = 2000; // 2 secondes
    this.MAX_PROCESSED_MESSAGES = 500; // Augmenté pour supporter 500 utilisateurs simultanés
  }

  /**
   * Initialise le gestionnaire avec les callbacks nécessaires
   * @param {Object} callbacks - Callbacks pour mettre à jour l'UI
   *   - setModel: fonction pour mettre à jour le modèle
   *   - setMessageToDisplay: fonction pour afficher un message
   *   - fetchModel: fonction pour récupérer le modèle complet
   *   - navigate: fonction pour naviguer
   *   - userEmail: email de l'utilisateur
   */
  async initialize(callbacks) {
    // Si déjà initialisé, mettre à jour seulement les callbacks
    if (this.isInitialized) {
      this.callbacks = { ...this.callbacks, ...callbacks };
      logger.info('GameRealtimeManager déjà initialisé, callbacks mis à jour');
      return;
    }

    this.callbacks = callbacks;

    try {
      // Créer les connexions WebSocket
      const connections = await createConnections(
        { userEmail: callbacks.userEmail },
        (message) => this.handleWebSocketMessage(message)
      );

      this.chatConnection = connections.chatConnection;
      this.gameConnection = connections.gameConnection;

      // Configurer les gestionnaires de reconnexion
      this.setupReconnectionHandlers();

      // Mettre à jour l'icône de statut après connexion réussie
      // Utiliser un petit délai pour s'assurer que le DOM est prêt
      if (this.statusIconTimeoutId) {
        clearTimeout(this.statusIconTimeoutId);
      }
      this.statusIconTimeoutId = setTimeout(() => {
        this.updateConnectionStatusIcon('success');
        this.statusIconTimeoutId = null;
      }, 100);

      this.isInitialized = true;
      logger.info('GameRealtimeManager initialisé avec succès');
    } catch (error) {
      logger.error('Erreur lors de l\'initialisation:', error);
      // Ne pas réinitialiser isInitialized en cas d'erreur pour éviter les boucles
      throw error;
    }
  }

  /**
   * Traite un message WebSocket reçu
   * TRAITEMENT IMMÉDIAT - pas de timeout
   */
  handleWebSocketMessage(rawMessage) {
    if (!this.isInitialized) {
      logger.warn('Manager non initialisé, message ignoré');
      return;
    }

    // Parser le message pour obtenir l'ID et vérifier les doublons
    let gameData;
    try {
      gameData = JSON.parse(rawMessage);
    } catch (e) {
      logger.error('Erreur lors du parsing du message:', e);
      return;
    }

    const messageId = `${gameData.action}-${gameData.message}-${gameData.value || ''}`;
    const now = Date.now();
    const timestamp = new Date().toISOString();
    logger.log('🎯 handleWebSocketMessage appelé - Message:', messageId, '| Time:', timestamp);

    // Vérifier si ce message a déjà été traité récemment (protection contre doublons)
    const lastProcessed = this.lastProcessedMessages.get(messageId);
    if (lastProcessed && (now - lastProcessed) < this.DUPLICATE_MESSAGE_WINDOW) {
      logger.warn('⚠️ Message dupliqué ignoré:', messageId, '| Dernier traitement il y a', now - lastProcessed, 'ms');
      return;
    }

    // Enregistrer ce message comme traité
    this.lastProcessedMessages.set(messageId, now);

    // Nettoyer les anciens messages (garder seulement MAX_PROCESSED_MESSAGES)
    // Utiliser une stratégie LRU simple: supprimer les plus anciens
    if (this.lastProcessedMessages.size > this.MAX_PROCESSED_MESSAGES) {
      // Supprimer les 10% les plus anciens pour éviter de nettoyer trop souvent
      const entriesToDelete = Math.floor(this.MAX_PROCESSED_MESSAGES * 0.1);
      let deleted = 0;
      for (const key of this.lastProcessedMessages.keys()) {
        if (deleted >= entriesToDelete) break;
        this.lastProcessedMessages.delete(key);
        deleted++;
      }
    }

    // TOUJOURS synchroniser avec window.model avant traitement pour éviter les états obsolètes
    if (typeof window !== 'undefined' && window.model) {
      this.currentModel = window.model;
    }

    // S'assurer que currentModel est disponible
    if (!this.currentModel) {
      logger.warn('Aucun modèle disponible, impossible de traiter le message');
      return;
    }

    // Préparer les callbacks avec le modèle actuel
    const processorCallbacks = {
      ...this.callbacks,
      currentModel: this.currentModel,
    };

    // Traiter le message immédiatement (utiliser gameData déjà parsé)
    const result = processMessage(rawMessage, processorCallbacks);

    // Log simple du résultat
    if (result.needsFullRefresh) {
      logger.log('🔄 Refresh complet demandé');
    }
    if (result.updatedModel) {
      logger.log('✅ Modèle mis à jour');
    }
    if (result.audioToPlay) {
      logger.log('🔊 Audio à jouer:', result.audioToPlay);
    }
    if (result.navigation) {
      logger.log('🧭 Navigation:', result.navigation.path, 'dans', result.navigation.delay, 'ms');
    }

    // Appliquer les résultats
    this.applyProcessingResult(result);
  }

  /**
   * Applique les résultats du traitement du message
   */
  applyProcessingResult(result) {
    // Mise à jour du modèle (incrémentale ou complète)
    if (result.updatedModel) {
      // updateGameState retourne déjà un modèle complet avec toutes les propriétés préservées
      const previousModelKeys = this.currentModel ? Object.keys(this.currentModel) : [];
      const previousBoulesCount = this.currentModel?.sequenceActivities?.length || 0;
      this.currentModel = result.updatedModel;
      const newModelKeys = Object.keys(result.updatedModel);
      const newBoulesCount = result.updatedModel?.sequenceActivities?.length || 0;

      logger.info('Modèle mis à jour. Propriétés avant:', previousModelKeys.length, 'après:', newModelKeys.length);
      logger.info('Nombre de boules avant:', previousBoulesCount, 'après:', newBoulesCount);

      // Mettre à jour window.model aussi pour synchronisation
      if (typeof window !== 'undefined') {
        window.model = result.updatedModel;
      }

      if (this.callbacks.setModel) {
        this.callbacks.setModel(result.updatedModel);
      }
    }

    // Refresh complet si nécessaire
    if (result.needsFullRefresh) {
      if (this.callbacks.fetchModel && !this.isFetchingModel) {
        // Si un délai est spécifié, attendre avant de faire le fetch
        const fetchModelWithDelay = () => {
          // Vérifier à nouveau le verrou pour éviter les appels concurrents
          if (this.isFetchingModel) {
            logger.warn('⚠️ fetchModel déjà en cours, skipping new call.');
            return;
          }

          this.isFetchingModel = true;
          logger.log('🔄 Appel de fetchModel()...');

          // fetchModel met à jour le modèle via setModel et retourne le modèle
          this.callbacks.fetchModel().then((newModel) => {
            this.isFetchingModel = false;
            if (newModel) {
              // Le modèle a été mis à jour dans fetchModel via setModel et gameRealtimeManager.setModel
              // Mais on s'assure que currentModel est bien à jour
              this.currentModel = newModel;
              logger.log('✅ Modèle mis à jour après fetchModel - Boules:', newModel?.sequenceActivities?.length, '| SequenceActif:', newModel?.tirage?.sequenceActif);
            } else {
              // Si fetchModel n'a pas retourné de modèle, récupérer depuis window.model
              if (typeof window !== 'undefined' && window.model) {
                this.currentModel = window.model;
                logger.log('✅ Modèle récupéré depuis window.model');
              }
            }

            // Vérifier la séquence après le fetch
            if (result.expectedSequence && newModel?.tirage?.sequenceActif !== result.expectedSequence) {
              logger.warn(`⚠️ La séquence attendue (${result.expectedSequence}) ne correspond pas à la séquence actuelle (${newModel?.tirage?.sequenceActif}). Nouvelle tentative dans 500ms...`);
              // Réessayer une fois après 500ms supplémentaires
              if (this.sequenceCheckTimeoutId) {
                clearTimeout(this.sequenceCheckTimeoutId);
              }
              this.sequenceCheckTimeoutId = setTimeout(() => {
                fetchModelWithDelay();
                this.sequenceCheckTimeoutId = null;
              }, 500);
            } else if (result.expectedSequence) {
              logger.log(`✅ Séquence confirmée: ${newModel?.tirage?.sequenceActif}`);
            }
          }).catch((error) => {
            this.isFetchingModel = false;
            logger.error('❌ Erreur lors du fetchModel:', error);
          });
        };

        if (result.refreshDelay) {
          logger.log(`⏳ Délai de ${result.refreshDelay}ms avant fetchModel pour laisser la DB se mettre à jour...`);
          // Nettoyer les timeouts précédents
          if (this.fetchModelTimeoutId) {
            clearTimeout(this.fetchModelTimeoutId);
          }
          if (this.sequenceCheckTimeoutId) {
            clearTimeout(this.sequenceCheckTimeoutId);
          }

          this.fetchModelTimeoutId = setTimeout(() => {
            fetchModelWithDelay();
            // Si on attend une séquence spécifique, vérifier après le fetch
            if (result.expectedSequence) {
              if (this.sequenceCheckTimeoutId) {
                clearTimeout(this.sequenceCheckTimeoutId);
              }
              this.sequenceCheckTimeoutId = setTimeout(() => {
                const currentModel = this.currentModel || (typeof window !== 'undefined' ? window.model : null);
                if (currentModel?.tirage?.sequenceActif !== result.expectedSequence) {
                  logger.warn(`⚠️ La séquence attendue (${result.expectedSequence}) ne correspond pas à la séquence actuelle (${currentModel?.tirage?.sequenceActif}). Nouvelle tentative dans 500ms...`);
                  // Réessayer une fois après 500ms supplémentaires
                  if (this.sequenceCheckTimeoutId) {
                    clearTimeout(this.sequenceCheckTimeoutId);
                  }
                  this.sequenceCheckTimeoutId = setTimeout(() => {
                    fetchModelWithDelay();
                    this.sequenceCheckTimeoutId = null;
                  }, 500);
                } else {
                  logger.log(`✅ Séquence confirmée: ${currentModel?.tirage?.sequenceActif}`);
                  this.sequenceCheckTimeoutId = null;
                }
              }, 200);
            }
            this.fetchModelTimeoutId = null;
          }, result.refreshDelay);
        } else {
          fetchModelWithDelay();
        }
      } else if (this.isFetchingModel) {
        logger.warn('⚠️ fetchModel déjà en cours, skipping new call.');
      } else {
        logger.error('❌ needsFullRefresh est true mais fetchModel callback n\'est pas disponible!');
      }
    }

    // Message à afficher
    if (result.messageToDisplay && this.callbacks.setMessageToDisplay) {
      // S'assurer que message est toujours une string valide
      const messageToDisplay = {
        ...result.messageToDisplay,
        message: result.messageToDisplay.message || '',
      };
      this.callbacks.setMessageToDisplay(messageToDisplay);
    }

    // Mise à jour différée du modèle (pour la boule frimée)
    if (result.delayedBallUpdate) {
      // Nettoyer le timeout précédent
      if (this.delayedBallUpdateTimeoutId) {
        clearTimeout(this.delayedBallUpdateTimeoutId);
      }

      this.delayedBallUpdateTimeoutId = setTimeout(() => {
        // Récupérer le modèle actuel (qui peut avoir été mis à jour entre-temps)
        const currentModel = this.currentModel || (typeof window !== 'undefined' ? window.model : null);

        if (currentModel && result.delayedBallUpdate.allBalls) {
          // Créer un gameData pour l'action "ball" avec la nouvelle boule
          const ballUpdateData = {
            action: GAME_ACTIONS.BALL,
            allBalls: result.delayedBallUpdate.allBalls,
          };

          // Mettre à jour le modèle de façon incrémentale
          if (canUpdateIncrementally(GAME_ACTIONS.BALL)) {
            const updatedModel = updateGameState(currentModel, ballUpdateData);
            this.currentModel = updatedModel;

            // Mettre à jour window.model aussi pour synchronisation
            if (typeof window !== 'undefined') {
              window.model = updatedModel;
            }

            if (this.callbacks.setModel) {
              this.callbacks.setModel(updatedModel);
            }

            logger.info('Modèle mis à jour après boule frimée. Nombre de boules:', updatedModel?.sequenceActivities?.length);
          }
        }
        this.delayedBallUpdateTimeoutId = null;
      }, result.delayedBallUpdate.delay);
    }

    // Navigation
    if (result.navigation) {
      if (result.navigation.delay) {
        // Nettoyer le timeout précédent
        if (this.navigationTimeoutId) {
          clearTimeout(this.navigationTimeoutId);
        }

        this.navigationTimeoutId = setTimeout(() => {
          if (this.callbacks.navigate) {
            this.callbacks.navigate(result.navigation.path);
          }
          this.navigationTimeoutId = null;
        }, result.navigation.delay);
      } else if (this.callbacks.navigate) {
        this.callbacks.navigate(result.navigation.path);
      }
    }
  }

  /**
   * Met à jour l'icône de statut de connexion
   * @param {string} status - 'success' ou 'danger'
   */
  updateConnectionStatusIcon(status) {
    const connectedStatusIndicator = document.getElementById('connectedStatusIcon');
    if (connectedStatusIndicator) {
      // Retirer les classes fill-*
      const classList = connectedStatusIndicator.classList;
      for (let i = classList.length - 1; i >= 0; i--) {
        if (classList[i].startsWith('fill-')) {
          connectedStatusIndicator.classList.remove(classList[i]);
        }
      }
      // Ajouter la nouvelle classe
      if (status === 'success') {
        connectedStatusIndicator.classList.add('fill-success');
      } else if (status === 'danger') {
        connectedStatusIndicator.classList.add('fill-danger');
      }
    }
  }

  /**
   * Configure les gestionnaires de reconnexion
   */
  setupReconnectionHandlers() {
    if (!this.chatConnection) return;

    this.chatConnection.onreconnecting(() => {
      logger.info('Reconnexion en cours...');
      this.updateConnectionStatusIcon('danger');
    });

    this.chatConnection.onreconnected(async () => {
      logger.info('Reconnecté avec succès');
      // Rejoindre le groupe
      await this.chatConnection.invoke('AddToGroup', 'bingoWendake');
      // Rafraîchir le modèle après reconnexion
      if (this.callbacks.fetchModel) {
        this.callbacks.fetchModel();
      }
      // Mettre à jour l'UI
      this.updateConnectionStatusIcon('success');
    });

    this.chatConnection.onclose(() => {
      logger.warn('Connexion fermée');
      this.updateConnectionStatusIcon('danger');
    });
  }

  /**
   * Met à jour le modèle actuel
   */
  setModel(model) {
    this.currentModel = model;
  }

  /**
   * Nettoie les ressources
   * Note: Ne ferme pas les connexions car elles sont partagées (singleton)
   * Les connexions seront fermées seulement à la fermeture de la page
   */
  async cleanup() {
    // Nettoyer tous les timeouts en cours pour éviter les fuites mémoire
    if (this.fetchModelTimeoutId) {
      clearTimeout(this.fetchModelTimeoutId);
      this.fetchModelTimeoutId = null;
    }
    if (this.sequenceCheckTimeoutId) {
      clearTimeout(this.sequenceCheckTimeoutId);
      this.sequenceCheckTimeoutId = null;
    }
    if (this.delayedBallUpdateTimeoutId) {
      clearTimeout(this.delayedBallUpdateTimeoutId);
      this.delayedBallUpdateTimeoutId = null;
    }
    if (this.navigationTimeoutId) {
      clearTimeout(this.navigationTimeoutId);
      this.navigationTimeoutId = null;
    }
    if (this.statusIconTimeoutId) {
      clearTimeout(this.statusIconTimeoutId);
      this.statusIconTimeoutId = null;
    }

    // Réinitialiser le verrou de fetchModel
    this.isFetchingModel = false;

    // Ne pas fermer les connexions car elles sont partagées entre les instances
    // Seulement réinitialiser les callbacks et le modèle
    this.callbacks = {};
    this.currentModel = null;
    // Ne pas mettre isInitialized à false pour éviter les réinitialisations multiples
    // this.isInitialized = false;
  }
}

// Export d'une instance singleton (optionnel)
export const gameRealtimeManager = new GameRealtimeManager();

